import * as C from '@shared/constants'
import { deserializeEmotes } from '@shared/utils'
import * as _ from '@/lodash'
import * as utils from '@/utils'
import * as M from '@/store/mutations'
import api from '@/api'

const state = {
    popperReferenceSelector: null,
    selectedEmote: null,
    defaultEmotes: [],
    bttvEmotes: [], // fetched once, immutable thereafter
    bttvChannelEmotes: [], // emotes specific to a channel.
    // the root channelId can change, but that doesn't mean the channel's
    // bttv emotes are immediately fetched. Use this property to
    // keep track of which channel's emotes are loaded
    bttvChannelId: null,
}

const mutations = {
    [M.SET_EMOTE_POPPER_REFERENCE_SELECTOR] (state, { selector }) {
        state.popperReferenceSelector = selector
    },
    [M.SET_SELECTED_EMOTE] (state, { emote }) {
        state.selectedEmote = emote
    },
    [M.SET_DEFAULT_EMOTES] (state, { emotes }) {
        state.defaultEmotes = emotes
    },
    [M.SET_BTTV_EMOTES] (state, { emotes }) {
        state.bttvEmotes = emotes
    },
    [M.SET_BTTV_CHANNEL_EMOTES] (state, { emotes, channelId }) {
        state.bttvChannelEmotes = emotes
        state.bttvChannelId = channelId
    }
}

const actions = {
    async updateEmoteReaction ({ state, getters, commit }, payload) {
        const { channelId, isBroadcaster } = getters
        const { suggestionId, userReacted, emote, } = payload

        // While handling a selectedEmote, it's simpler to pass around a
        // single, primitive value (emojiOrEmoteId), and then lookup the alias
        // afterwards. Or I just got lazy. Regardless, the emote alias
        // is looked up here.
        const alias = getters.findEmoteAlias(emote)
        payload.alias = alias
        if (isBroadcaster) {
            payload.broadcasterReacted = userReacted
        }

        // Update UI before the api call to feel more responsive.
        commit(M.SET_EMOTE_REACTION, payload)

        const url = `channels/${channelId}/suggestions/${suggestionId}/reactions`
        const opts = { emote, alias, userReacted }
        await api.put(url, opts)
    },
    async fetchBTTVEmotes ({ state, getters, commit }, { types }) {
        const { channelId } = getters

        const promises = types.map(type => {
            const opts = {
                params: { type },
                transformResponse: deserializeEmotes
            }
            if (type === C.BTTV_CHANNEL) {
                opts.params.channelId = channelId
            }
            return api.get('emotes', opts)
        })
        const results = await Promise.all(promises)

        const emoteSize = C.EMOTE_SIZE_IN_PICKER

        let emotes = results.flatMap(res => {
            const { emotes = [] } = res.data

            return emotes.map(e => {
                // Conform to v-emoji-picker data structure.
                return Object.freeze({
                    data: utils.bttvEmoteIdToImgTag(e.id, e.code, emoteSize),
                    id: e.id,
                    category: C.BTTV,
                    aliases: [e.code]
                })
            })
        })
        // e.aliases can be undefined, wtf? w/e, just handle it.
        emotes = _.uniqBy(emotes, e => e.aliases?.[0])

        const mutation = types[0] === C.BTTV_CHANNEL
            ? M.SET_BTTV_CHANNEL_EMOTES : M.SET_BTTV_EMOTES
        commit(mutation, { emotes: Object.freeze(emotes), channelId })
    },
    async fetchBTTVChannelEmotes ({ dispatch }) {
        return dispatch('fetchBTTVEmotes', { types: [C.BTTV_CHANNEL] })
    }
}

const getters = {
    popperReferenceSelector: s => s.popperReferenceSelector,
    selectedEmote: s => s.selectedEmote,
    defaultEmotes: s => s.defaultEmotes,
    bttvEmotes: s => s.bttvEmotes,
    bttvChannelEmotes: s => s.bttvChannelEmotes,
    bttvChannelId: s => s.bttvChannelId,
    // https://stackoverflow.com/questions/48865710/spread-operator-vs-array-concat
    // .concat is faster than ...spread
    allEmotes: s => [].concat(s.bttvChannelEmotes, s.bttvEmotes, s.defaultEmotes),
    findEmoteAlias: (s, getters) => emojiOrEmoteId => {
        const emote = getters.allEmotes.find(e => {
            return e.id === emojiOrEmoteId || e.emoji === emojiOrEmoteId
        })

        return emote?.aliases[0] || '(unknown emote)'
    },
    findEmoteReaction: (state, getters) => (sid, emote) => {
        const suggestion = getters.findSuggestion(sid)
        return suggestion?.emotes?.find(e => e.emote === emote)
    }
}

export default {
    state,
    mutations,
    actions,
    getters
}
